import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { PaginationResponse } from '@/types/pagination';
import type { GetAllClaimSetsPayload } from '@/domains/claimSets/store/types/AllClaimsState';

export type AllClaimSetListState = {
  list: NormalizedV2Response[];
  counters: {
    allClaimSets: number;
    myClaimSets: number;
  };
  statuses: {
    isLoading: boolean;
  };
  pagination: PaginationResponse;
};

const initialState: AllClaimSetListState = {
  list: [],
  counters: {
    allClaimSets: 0,
    myClaimSets: 0,
  },
  statuses: {
    isLoading: false,
  },
  pagination: {
    numberOfItems: 0,
    first: '1',
    last: undefined,
    previous: '0',
    next: '0',
    page: 1,
    perPage: 20,
  },
};

const allClaimSetsListSlice = createSlice({
  name: 'allClaimSetsListSlice',
  initialState,
  reducers: {
    getAllClaimSetsList: (state: AllClaimSetListState, _payload: PayloadAction<GetAllClaimSetsPayload>) => {
      state.statuses.isLoading = true;
      state.pagination = initialState.pagination;
    },
    getAllClaimSetsListSuccess: (
      state: AllClaimSetListState,
      {
        payload: { listings, counters, pagination, shouldRewrite },
      }: PayloadAction<{
        listings: NormalizedV2Response[];
        counters: { allClaimSets?: number };
        pagination: PaginationResponse;
        shouldRewrite?: boolean;
      }>,
    ) => {
      state.counters = { ...state.counters, ...counters };
      state.statuses.isLoading = false;
      state.list = shouldRewrite ? listings : [...state.list, ...listings];
      state.pagination = pagination;
    },
    incrementAllClaimSetsListPage: (state: AllClaimSetListState, _payload: PayloadAction<GetAllClaimSetsPayload>) => {
      state.statuses.isLoading = true;
      state.pagination.page += 1;
    },
    updateAllClaimSetsCounters: () => {},
    updateAllClaimSetsCountersSuccess: (
      state: AllClaimSetListState,
      { payload }: PayloadAction<{ allClaimSets: number; myClaimSets: number }>,
    ) => {
      state.counters = payload;
    },
    resetAllClaimSetsListSlice: () => initialState,
  },
});

export const {
  getAllClaimSetsList,
  getAllClaimSetsListSuccess,
  incrementAllClaimSetsListPage,
  updateAllClaimSetsCounters,
  updateAllClaimSetsCountersSuccess,
  resetAllClaimSetsListSlice,
} = allClaimSetsListSlice.actions;

export const allClaimSetsListReducer = allClaimSetsListSlice.reducer;
export const allClaimSetsListReducerPath = allClaimSetsListSlice.reducerPath;
